import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from '@arco-design/web-react';
import { adminUpstreamErrorLogApi } from '@/api';
import type { PaginatedResult } from '@/types/api';
import type {
  UpstreamErrorLogDetail,
  UpstreamErrorLogItem,
  UpstreamErrorLogQuery,
  UpstreamErrorType,
} from '@/types/upstreamErrorLog';

const ERROR_TYPE_COLOR: Record<string, string> = {
  TIMEOUT: 'orange',
  HTTP_ERROR: 'red',
  HTTP_STATUS: 'red',
  JSON_INVALID: 'magenta',
  BUSINESS: 'gold',
};

const ERROR_TYPES: UpstreamErrorType[] = [
  'TIMEOUT',
  'HTTP_ERROR',
  'HTTP_STATUS',
  'JSON_INVALID',
  'BUSINESS',
];

export default function UpstreamErrorLogsView() {
  const [data, setData] = useState<PaginatedResult<UpstreamErrorLogItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<UpstreamErrorLogQuery>({ page: 1, pageSize: 20 });
  const [detail, setDetail] = useState<UpstreamErrorLogDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminUpstreamErrorLogApi
      .list(query)
      .then(setData)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const d = await adminUpstreamErrorLogApi.detail(id);
      setDetail(d);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <div className="admin-page__title">上游错误日志</div>
          <div className="admin-page__subtitle">
            Newgen 上游调用失败记录（超时 / HTTP / 业务错误等），便于排查与跟进
          </div>
        </div>
      </div>

      <div className="admin-card">
        <Form
          layout="inline"
          style={{ marginBottom: 12 }}
          initialValues={query}
          onSubmit={(values) => setQuery({ ...query, ...values, page: 1 })}
        >
          <Form.Item field="apiPath" label="接口路径">
            <Input placeholder="/virtualcard/..." allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item field="errorType" label="错误类型">
            <Select placeholder="全部" allowClear style={{ width: 160 }}>
              {ERROR_TYPES.map((t) => (
                <Select.Option key={t} value={t}>
                  {t}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item field="upstreamErrorCode" label="上游错误码">
            <Input placeholder="精确匹配" allowClear style={{ width: 140 }} />
          </Form.Item>
          <Form.Item field="keyword" label="单号/卡 ID">
            <Input placeholder="out_trade_no / card_id" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                查询
              </Button>
              <Button onClick={() => setQuery({ page: 1, pageSize: 20 })}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <Table<UpstreamErrorLogItem>
          loading={loading}
          rowKey="id"
          data={data?.items ?? []}
          pagination={{
            current: query.page ?? 1,
            pageSize: query.pageSize ?? 20,
            total: data?.total ?? 0,
            onChange: (p) => setQuery({ ...query, page: p }),
            showTotal: true,
          }}
          columns={[
            { title: '时间', dataIndex: 'createdAt', width: 180 },
            {
              title: '接口',
              dataIndex: 'apiPath',
              render: (v) => <span className="admin-mono">{v}</span>,
            },
            {
              title: '类型',
              dataIndex: 'errorType',
              width: 120,
              render: (v: string) => (
                <Tag color={ERROR_TYPE_COLOR[v] ?? 'gray'}>{v}</Tag>
              ),
            },
            {
              title: '上游码',
              dataIndex: 'upstreamErrorCode',
              width: 100,
              render: (v) => v || '-',
            },
            {
              title: 'HTTP',
              dataIndex: 'httpStatus',
              width: 72,
              align: 'right',
              render: (v) => (v != null ? v : '-'),
            },
            {
              title: '错误信息',
              dataIndex: 'errorMessage',
              ellipsis: true,
              render: (v) => v || '-',
            },
            {
              title: '商户单号',
              dataIndex: 'outTradeNo',
              width: 160,
              render: (v) => v || '-',
            },
            {
              title: '卡 ID',
              dataIndex: 'upstreamCardId',
              width: 120,
              render: (v) =>
                v ? <span className="admin-mono">{v}</span> : '-',
            },
            {
              title: '操作',
              dataIndex: 'op',
              width: 88,
              render: (_, row) => (
                <Button size="mini" onClick={() => openDetail(row.id)}>
                  详情
                </Button>
              ),
            },
          ]}
        />
      </div>

      <Drawer
        title="上游错误详情"
        width={720}
        visible={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
      >
        {detailLoading || !detail ? (
          <Typography.Text type="secondary">加载中…</Typography.Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <div>
              <Tag color={ERROR_TYPE_COLOR[detail.errorType] ?? 'gray'}>
                {detail.errorType}
              </Tag>{' '}
              <span className="admin-mono">{detail.apiPath}</span>
            </div>
            <Typography.Text type="secondary">
              时间：{detail.createdAt}
              {detail.httpStatus != null ? ` · HTTP ${detail.httpStatus}` : ''}
              {detail.upstreamErrorCode
                ? ` · 上游码 ${detail.upstreamErrorCode}`
                : ''}
            </Typography.Text>
            {(detail.outTradeNo || detail.upstreamCardId) && (
              <Typography.Text type="secondary">
                {detail.outTradeNo ? `商户单号：${detail.outTradeNo}` : ''}
                {detail.outTradeNo && detail.upstreamCardId ? ' · ' : ''}
                {detail.upstreamCardId ? `卡 ID：${detail.upstreamCardId}` : ''}
              </Typography.Text>
            )}
            {detail.errorMessage && (
              <div>
                <div style={{ marginBottom: 4, color: 'var(--admin-text-tertiary)' }}>
                  错误信息：
                </div>
                <pre
                  style={{
                    background: 'var(--admin-bg-page)',
                    padding: 12,
                    borderRadius: 6,
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {detail.errorMessage}
                </pre>
              </div>
            )}
            {detail.requestParams != null && (
              <div>
                <div style={{ marginBottom: 4, color: 'var(--admin-text-tertiary)' }}>
                  请求参数（敏感字段已脱敏）：
                </div>
                <pre
                  style={{
                    background: 'var(--admin-bg-page)',
                    padding: 12,
                    borderRadius: 6,
                    margin: 0,
                    maxHeight: 240,
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(detail.requestParams, null, 2)}
                </pre>
              </div>
            )}
            {detail.responseBody && (
              <div>
                <div style={{ marginBottom: 4, color: 'var(--admin-text-tertiary)' }}>
                  响应内容：
                </div>
                <pre
                  style={{
                    background: 'var(--admin-bg-page)',
                    padding: 12,
                    borderRadius: 6,
                    margin: 0,
                    maxHeight: 360,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {detail.responseBody}
                </pre>
              </div>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
}
